import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import CtNotSetNote from "@/components/CtNotSetNote";   // Batch 27 · D (M37)
import useMissingFields, { FieldMessage, RequiredMark } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1
import { fmtDateTimeFirm } from "@/lib/dt";
import api, { toErrMsg } from "@/lib/api";
import { fetchPreview, previewKey } from "@/lib/materialityPreview";   // Step 3 · rule 3 (B-3e): the server rounds, never the screen
import { useI18n } from "@/contexts/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { MatRangeNote, matReasonsBody, useEngagementMethodology } from "@/components/methodology/MethodologyRange";   // STEP 7e-2 · S2

const BENCHMARKS = [
  ["Profit before tax", "pbt"], ["Profit before zakat", "pbz"], ["Revenue", "revenue"],   // Batch 33 · T4: the KSA term
  ["Total assets", "totalAssets"], ["Equity", "equity"],
  ["Total expenses", "expenses"], ["Other", "other"],
];

// T3 (B3 Fix List 1) — revision history shows the human field label
// pulled from i18n, not the raw model key.
const FIELD_LABEL_KEYS = {
  benchmark: "materiality.fields.benchmark",
  amount: "materiality.fields.amount",
  om_pct: "materiality.fields.omPct",
  pm_pct: "materiality.fields.pmPct",
  ct_pct: "materiality.fields.ctPct",
  rationale: "materiality.fields.rationale",
  overall_materiality: "materiality.fields.overallMateriality",
  performance_materiality: "materiality.fields.performanceMateriality",
  clearly_trivial: "materiality.fields.clearlyTrivial",
  migration: "materiality.fields.migration",
};

function fieldLabel(t, k) {
  const key = FIELD_LABEL_KEYS[k];
  if (!key) return k;
  const label = t(key);
  // if the i18n lookup returned the raw key back, fall back to the
  // stored model key so the UI never renders "materiality.fields.xxx".
  return label && label !== key ? label : k;
}

// Batch 27 · Add-on 1 — the same entry the server returns for a change without a reason
const REASON_MISSING = { field: "reason", label_en: "Reason for revision", label_ar: "سبب التعديل",
  reason_en: "Enter the reason for the materiality change before saving (it goes on the revision log).",
  reason_ar: "اكتب سبب التغيير في المادية قبل الحفظ (يُسجَّل في سجل المراجعات)." };
// Batch 33 · B33A-2 — the server's override check (overall typed ≠ benchmark × %), shown before the request on a first save
const OVERRIDE_MISSING = { field: "override_reason", label_en: "Reason for the change", label_ar: "سبب التغيير",
  reason_en: "Overriding the calculated overall figure needs a reason.",
  reason_ar: "تجاوز الرقم الإجمالي المحسوب يحتاج إلى سبب." };

// Batch A1 — Materiality figures block. Rendered above the 105(a) form
// body. Every change after first save requires a reason (logged).
const MaterialityFiguresPanel = React.forwardRef(function MaterialityFiguresPanel({ engagementId, onDirty }, ref) {
  const histCols = useResizableColumns({ storageKey: "materiality-history", defaults: [150, 160, 170, 130, 130, 300], mins: [110, 100, 110, 90, 90, 140] });   // Step 11d · add-on: adjustable columns
  const { t, lang } = useI18n();
  const [doc, setDoc] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedDoc, setSavedDoc] = useState(null);     // Batch 9 · A7: what the server holds, to know whether the form is dirty
  const [overallTyped, setOverallTyped] = useState(false);   // Batch 33A · P49-2: the auditor typed the overall on a seeded row
  const [overrideReason, setOverrideReason] = useState("");   // Batch 33 · B33A-2: the override reason typed on a first save
  const mf = useMissingFields(lang);                  // Batch 27 · Add-on 1: the missing field in red, its reason under it
  const meth = useEngagementMethodology(engagementId);   // STEP 7e-2 · S2: the firm range of the linked methodology version
  const [methReasons, setMethReasons] = useState({});
  const [priorYear, setPriorYear] = useState(null);   // Batch 10.1 · R2 — last year's 105(a) figures, for reference only
  useEffect(() => {
    if (!engagementId) return;
    api.get(`/engagements/${engagementId}/prior-year`, { _silent: true }).then((r) => setPriorYear(r.data)).catch(() => setPriorYear(null));
  }, [engagementId]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/engagements/${engagementId}/materiality-figures`);
      setDoc(r.data); setSavedDoc(r.data); setOverallTyped(false); setOverrideReason("");
    } catch (e) { toast.error(toErrMsg(e)); }
  }, [engagementId]);
  useEffect(() => { if (engagementId) load(); }, [engagementId, load]);

  // Step 3 · rule 3 (B-3e): the server's figures for what is typed (calculated overall, PM, CT) — the screen never rounds a %
  const [pv, setPv] = useState(null);                 // { key, data } — data from POST …/materiality-figures/preview
  const pvKey = doc ? previewKey(doc) : "";
  useEffect(() => {
    if (!doc || !engagementId) return undefined;
    let live = true;
    fetchPreview(engagementId, doc).then((data) => { if (live) setPv({ key: pvKey, data }); })
      .catch(() => { if (live) setPv(null); });
    return () => { live = false; };
  }, [pvKey, engagementId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => { setDoc({ ...doc, [k]: v }); if (onDirty) onDirty(); };   // Batch 9 · A7: the form Save lights up
  // Batch 33 · B33A-1: the server says when the next save is the first one (a seeded row — Batch 33A — or a migrated row never
  // saved on 105(a)); before, the screen guessed from "no benchmark and no rationale" and hid the reason field the server wanted
  const firstSavePending = !!doc && (!!doc.seeded_from_create || !!doc.first_save_pending);
  const isFirstSave = firstSavePending;
  const FIELDS = ["benchmark", "benchmark_amount", "pct_applied", "overall_materiality", "overall_override_reason", "pm_pct", "ct_pct", "rationale", "specific_materiality"];
  const isDirty = !!doc && !!savedDoc && FIELDS.some((k) => JSON.stringify(doc[k] ?? null) !== JSON.stringify(savedDoc[k] ?? null));
  // Batch 9 · A7 (F-61): the form's Save button calls this — nothing to save → true; a later change without a reason → false + message
  React.useImperativeHandle(ref, () => ({
    saveFromForm: async () => {
      if (!doc || !isDirty) return true;
      if (!isFirstSave && !reason.trim()) {
        mf.apply({ missing_fields: [REASON_MISSING] });
        return false;
      }
      return await save();
    },
    isDirty: () => isDirty,
  }), [doc, savedDoc, reason, overrideReason, overallTyped, isFirstSave, isDirty, pv]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!doc) return null;
  // Batch 9 · A8 (F-62): the calculated figure is ALWAYS benchmark × % / 100 rounded to 2 decimals — no other rounding;
  // an override (a figure entered by hand, with its reason) is shown as an override, never labelled "calculated"
  // Step 3 · B-3e: benchmark × % comes from the server (materiality.pct_figure); until its answer for the figures typed is in,
  // the screen keeps the figures the server holds (nothing is computed here)
  const pvData = pv && pv.key === pvKey ? pv.data : null;
  const calcExact = pvData ? Number(pvData.calculated_overall || 0) : null;
  // Batch 33A · P49-2: on a row seeded at engagement create (never saved on 105(a)) the overall follows benchmark × % once both
  // are entered, until the auditor types an overall; before that the create-time figures stay on screen
  // Batch 33 · B33A-1: the same for a migrated row never saved (first_save_pending)
  const seedFollows = firstSavePending && !overallTyped && calcExact > 0;
  const derivedOverall = (doc.overall_override_reason || (firstSavePending && !seedFollows))
    ? Number(doc.overall_materiality || 0) : calcExact;
  // Batch 33 · B33A-2: on a first save an overall that differs from benchmark × % needs the server's override reason — the field
  // shows here (before, the screen had no input for it and hid the reason field on a first save, so the save could not pass)
  const overallShown = seedFollows ? calcExact : Number(doc.overall_materiality ?? derivedOverall);
  const needsOverride = isFirstSave && calcExact != null && !doc.overall_override_reason && Math.abs(overallShown - calcExact) >= 0.005;
  // Step 3 · B-3e: PM / CT are the server's figures — on the calculated overall, or on the entered overall (an override / a
  // seeded or migrated figure not following benchmark × %); while the server's answer is pending, the figures it holds
  const onCalc = !(doc.overall_override_reason || (firstSavePending && !seedFollows));
  const pvFig = pvData ? (onCalc ? pvData.on_calculated : (pvData.on_entered || {})) : null;
  const pm = Number((pvFig || doc).performance_materiality || 0);
  const ct = Number((pvFig || doc).clearly_trivial || 0);
  // App-wide numeric format: Western digits + thousand separators in
  // BOTH locales (spec: consistency). Avoid Arabic-Indic digit
  // presets which switch to Eastern digits in most browsers.
  const NF = { format: (n) => fmtNumber(n) };   // STEP 3 · rule 2 · B-2e
  const fmt = (n) => NF.format(Number(n || 0));
  const parseNum = (s) => {
    const cleaned = String(s ?? "").replace(/[^\d.\-]/g, "");
    return cleaned === "" ? 0 : Number(cleaned);
  };
  // C1-B1 — hide system-generated / migration reasons on screen.
  // Real user-authored reasons pass through unchanged.
  const isSystemReason = (raw) => {
    if (!raw) return false;
    const k = String(raw).toLowerCase();
    return k.startsWith("migrated")
        || k.includes("risk_assessment")
        || k.includes("engagement_fields")
        || k === "seeded from new engagement"
        || k.startsWith("seed:") || k.startsWith("system:");
  };
  const overrideLabel = (raw) => (isSystemReason(raw) ? "" : (raw || ""));
  // Localize revision-history date/time in the user's locale.
  const fmtWhen = (iso) => {
    if (!iso) return "";
    try { return fmtDateTimeFirm(iso); } catch { return iso; }
  };
  // Pretty-print old / new values for the history table: numbers get
  // separators, {overall, pm} objects are unpacked, everything else is
  // a compact JSON string.
  const fmtVal = (v) => {
    if (v == null) return "—";
    if (typeof v === "number") return fmt(v);
    if (typeof v === "string") return v;
    if (typeof v === "object") {
      // Common migration shape: { engagement: {overall, pm}, ... }
      if (v.overall != null && v.pm != null)
        return `${t("materiality.overall")}: ${fmt(v.overall)} · ${t("materiality.pm")}: ${fmt(v.pm)}`;
      if (v.engagement?.overall != null)
        return `${t("materiality.overall")}: ${fmt(v.engagement.overall)} · ${t("materiality.pm")}: ${fmt(v.engagement.pm)}`;
      const parts = Object.entries(v).map(([k, val]) => `${k}: ${fmtVal(val)}`);
      return parts.join(" · ");
    }
    return String(v);
  };

  // Specific materiality helpers (Batch A1 spec sheet 2).
  const specs = Array.isArray(doc.specific_materiality) ? doc.specific_materiality : [];
  const setSpecs = (rows) => { setDoc({ ...doc, specific_materiality: rows }); if (onDirty) onDirty(); };
  const addSpec = () => setSpecs([...specs, { fs_head: "", amount: 0, reason: "" }]);
  const updSpec = (i, k, v) => setSpecs(specs.map((r, ii) => ii === i ? { ...r, [k]: v } : r));
  const delSpec = (i) => setSpecs(specs.filter((_, ii) => ii !== i));

  const save = async () => {
    // Step 3 · B-3e: a save before the server's preview answered asks the server for benchmark × % first (never computed here)
    let calc = calcExact, follows = seedFollows, needOv = needsOverride;
    if (calc == null) {
      try { calc = Number((await fetchPreview(engagementId, doc))?.calculated_overall || 0); } catch { calc = 0; }
      follows = firstSavePending && !overallTyped && calc > 0;
      needOv = isFirstSave && !doc.overall_override_reason
        && Math.abs((follows ? calc : Number(doc.overall_materiality ?? calc)) - calc) >= 0.005;
    }
    if (needOv && !overrideReason.trim()) {     // Batch 33 · B33A-2: the server would refuse — say so before the request
      mf.apply({ missing_fields: [OVERRIDE_MISSING] });
      return false;
    }
    setSaving(true);
    try {
      const body = {
        benchmark: doc.benchmark, benchmark_amount: Number(doc.benchmark_amount || 0),
        pct_applied: Number(doc.pct_applied || 0),
        overall_materiality: follows ? calc : Number(doc.overall_materiality || 0),
        overall_override_reason: needOv ? overrideReason.trim() : (doc.overall_override_reason || null),
        pm_pct: Number(doc.pm_pct || 0), ct_pct: Number(doc.ct_pct || 0),
        rationale: doc.rationale, specific_materiality: doc.specific_materiality || [],
        reason: isFirstSave ? null : (reason || null),
        methodology_reasons: matReasonsBody(meth.ms, meth.devs, doc, methReasons),   // STEP 7e-2 · S2
      };
      const r = await api.put(`/engagements/${engagementId}/materiality-figures`, body);
      setDoc(r.data); setSavedDoc(r.data); setReason(""); setOverrideReason(""); setOverallTyped(false); mf.reset();
      setMethReasons({}); meth.reload();   // STEP 7e-2 · S3: the deviations the save recorded / closed
      toast.success(t("materiality.savedToast") || "Materiality saved");
      window.dispatchEvent(new CustomEvent("materiality-changed", { detail: r.data }));   // Batch 9 · A7: pages reading 105(a) refresh
      return true;
    } catch (e) { if (!mf.apply(e)) toast.error(toErrMsg(e)); return false; }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="materiality-figures-panel">
      <CardHeader><CardTitle>{t("materiality.figuresTitle")}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {priorYear?.available && priorYear.materiality && Object.keys(priorYear.materiality).length > 0 && (
          /* Batch 10.1 · R2 — materiality is not carried forward; last year's figures are shown for reference only */
          <div className="rounded border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-900"
               data-testid="mat-prior-year">
            <div className="font-medium">{t("materiality.priorYearRef").replace("{fy}", String(priorYear.engagement?.financial_year || ""))}</div>
            <div className="mt-1 flex flex-wrap gap-x-6 gap-y-1">
              {[["overall_materiality", "materiality.priorOverall"], ["performance_materiality", "materiality.priorPm"],
                ["clearly_trivial", "materiality.priorCt"]].map(([k, label]) => (
                <span key={k} data-testid={`mat-prior-${k}`}>{t(label)}: <span className="font-mono">
                  {priorYear.materiality[k] != null ? fmtNumber(Number(priorYear.materiality[k])) : "—"}</span></span>
              ))}
              {priorYear.materiality.benchmark && <span>{t("materiality.priorBenchmark")}: {priorYear.materiality.benchmark}</span>}
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label>{t("materiality.benchmark")}</Label>
            <Select value={doc.benchmark || ""} onValueChange={(v) => set("benchmark", v)}>
              <SelectTrigger data-testid="mat-benchmark"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {BENCHMARKS.map(([v, k]) => (
                  <SelectItem key={v} value={v}>{t(`materiality.benchmarks.${k}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("materiality.benchmarkAmount")}</Label>
            <Input type="text" inputMode="decimal"
                     value={fmt(doc.benchmark_amount || 0)}
                     onChange={(e) => set("benchmark_amount", parseNum(e.target.value))}
                     data-testid="mat-benchmark-amount" />
          </div>
          <div>
            <Label>{t("materiality.pctApplied")}</Label>
            <Input type="number" step="0.01"
                     value={doc.pct_applied == null || doc.pct_applied === "" ? 0 : Number(doc.pct_applied)}
                     onChange={(e) => set("pct_applied", e.target.value)}
                     data-testid="mat-pct-applied" />
            <MatRangeNote field="pct_applied" ms={meth.ms} devs={meth.devs} doc={doc} reasons={methReasons} setReasons={setMethReasons} mf={mf} t={t} />
          </div>
          <div>
            <Label>{t("materiality.overall")}</Label>
            <Input type="text" inputMode="decimal"
                     value={fmt(seedFollows ? calcExact : (doc.overall_materiality ?? derivedOverall))}
                     onChange={(e) => { setOverallTyped(true); set("overall_materiality", parseNum(e.target.value)); }}
                     data-testid="mat-overall" />
            {needsOverride && (
              <div className="mt-1">
                {/* Batch 33 · B33A-2: the override reason on a first save (the server's override check) */}
                <Label className="text-[11px]">{t("materiality.overrideReason")}<RequiredMark lang={lang} show /></Label>
                <Input value={overrideReason}
                       onChange={(e) => { setOverrideReason(e.target.value); mf.filled("override_reason", e.target.value); }}
                       {...mf.props("override_reason")} data-testid="mat-override-reason"
                       placeholder={t("materiality.overrideReasonHelp")} />
                <FieldMessage mf={mf} field="override_reason" testid="mat-override-reason-missing" />
              </div>
            )}
            <div className="mt-1 text-[10px] text-muted-foreground" data-testid="mat-overall-calc">
              {t("materiality.overallCalc").replace("{v}", lang === "ar"
                ? `${fmt(Number(doc.pct_applied || 0))} % × ${fmt(Number(doc.benchmark_amount || 0))} = ${calcExact == null ? "…" : fmt(calcExact)} (مقرّب إلى منزلتين عشريتين — لا يوجد تقريب آخر)`
                : `${fmt(Number(doc.pct_applied || 0))} % × ${fmt(Number(doc.benchmark_amount || 0))} = ${calcExact == null ? "…" : fmt(calcExact)} (rounded to 2 decimals — no other rounding is applied)`)}
            </div>
            {doc.overall_override_reason && calcExact != null && Math.abs(Number(doc.overall_materiality || 0) - calcExact) >= 0.005 && (
              <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-300" data-testid="mat-overall-override">
                {lang === "ar"
                  ? `المادية الإجمالية ${fmt(doc.overall_materiality)} مُدخلة يدويًا وتتجاوز الرقم المحسوب ${fmt(calcExact)}`
                  : `Overall materiality ${fmt(doc.overall_materiality)} is an entered figure that overrides the calculated ${fmt(calcExact)}`}
              </div>
            )}
            {doc.overall_override_reason && overrideLabel(doc.overall_override_reason) && (
              <div className="mt-1 text-[10px] text-amber-700 dark:text-amber-300"
                   data-testid="mat-overall-override-note">
                {t("materiality.overrideNote").replace("{reason}", overrideLabel(doc.overall_override_reason))}
              </div>
            )}
          </div>
          <div>
            <Label>{t("materiality.pmPct")}</Label>
            <Input type="number" step="0.01"
                     value={doc.pm_pct == null || doc.pm_pct === "" ? 0 : Number(doc.pm_pct)}
                     onChange={(e) => set("pm_pct", e.target.value)}
                     data-testid="mat-pm-pct" />
            <MatRangeNote field="pm_pct" ms={meth.ms} devs={meth.devs} doc={doc} reasons={methReasons} setReasons={setMethReasons} mf={mf} t={t} />
          </div>
          <div>
            <Label>{t("materiality.pm")}</Label>
            <Input readOnly value={fmt(pm)} data-testid="mat-pm" />
          </div>
          <div>
            <Label>{t("materiality.ctPct")}</Label>
            <Input type="number" step="0.01"
                     value={doc.ct_pct == null || doc.ct_pct === "" ? 0 : Number(doc.ct_pct)}
                     onChange={(e) => set("ct_pct", e.target.value)}
                     data-testid="mat-ct-pct" />
            <MatRangeNote field="ct_pct" ms={meth.ms} devs={meth.devs} doc={doc} reasons={methReasons} setReasons={setMethReasons} mf={mf} t={t} />
          </div>
          <div>
            <Label>{t("materiality.ct")}</Label>
            <Input readOnly value={fmt(ct)} data-testid="mat-ct" />
          </div>
        </div>
        <CtNotSetNote value={ct} onForm testid="mat-ct-not-set" />
        {/* Specific materiality — spec sheet 2 rows */}
        <div className="rounded border p-3" data-testid="mat-specific-block">
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-sm">{t("materiality.specificTitle")}</Label>
            <Button type="button" size="sm" variant="outline" onClick={addSpec}
                     data-testid="mat-specific-add">
              + {t("materiality.specificAdd")}
            </Button>
          </div>
          {specs.length === 0 ? (
            <div className="text-[11px] text-muted-foreground">{t("materiality.specificEmpty")}</div>
          ) : (
            <div className="space-y-2">
              {specs.map((r, i) => (
                <div key={i} className="grid grid-cols-12 items-end gap-2"
                     data-testid={`mat-specific-row-${i}`}>
                  <div className="col-span-4">
                    <Label className="text-[10px]">{t("materiality.specificFsHead")}</Label>
                    <Input value={r.fs_head || ""}
                             onChange={(e) => updSpec(i, "fs_head", e.target.value)}
                             data-testid={`mat-specific-${i}-fshead`} />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px]">{t("materiality.specificAmount")}</Label>
                    <Input type="number" value={r.amount || 0}
                             onChange={(e) => updSpec(i, "amount", Number(e.target.value))}
                             data-testid={`mat-specific-${i}-amount`} />
                  </div>
                  <div className="col-span-4">
                    <Label className="text-[10px]">{t("materiality.specificReason")}</Label>
                    <Input value={r.reason || ""}
                             onChange={(e) => updSpec(i, "reason", e.target.value)}
                             data-testid={`mat-specific-${i}-reason`} />
                  </div>
                  <div className="col-span-1">
                    <Button size="sm" variant="ghost" onClick={() => delSpec(i)}
                             data-testid={`mat-specific-${i}-del`}>×</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <Label>{t("materiality.rationale")}</Label>
          <Textarea value={doc.rationale || ""} onChange={(e) => set("rationale", e.target.value)}
                       data-testid="mat-rationale" />
        </div>
        {!isFirstSave && (
          <div>
            {/* Batch 27 · Add-on 1: required (marked) as soon as a saved figure changes */}
            <Label>{t("materiality.reason")}<RequiredMark lang={lang} show={isDirty} /></Label>
            <Input value={reason} onChange={(e) => { setReason(e.target.value); mf.filled("reason", e.target.value); }}
                     {...mf.props("reason")} data-testid="mat-reason" placeholder={t("materiality.reason")} />
            <FieldMessage mf={mf} field="reason" testid="mat-reason-missing" />
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="mat-save">
            {saving ? "…" : t("materiality.revise")}
          </Button>
        </div>
        {(doc.revision_history || []).length > 0 && (
          <div>
            <div className="mb-1 text-sm font-semibold">{t("materiality.history")}</div>
            <div className="max-h-56 overflow-auto rounded border" data-testid="mat-history">
              <ResizableTable cols={histCols} className="w-full text-[12px]">
                <thead className="border-b bg-slate-50 text-[10px] uppercase text-muted-foreground dark:bg-slate-900/40">
                  <tr>
                    <ResizableTh index={0} className="px-2 py-1 text-start">{t("materiality.historyCols.when")}</ResizableTh>
                    <ResizableTh index={1} className="px-2 py-1 text-start">{t("materiality.historyCols.who")}</ResizableTh>
                    <ResizableTh index={2} className="px-2 py-1 text-start">{t("materiality.historyCols.field")}</ResizableTh>
                    <ResizableTh index={3} className="px-2 py-1 text-start">{t("materiality.historyCols.old")}</ResizableTh>
                    <ResizableTh index={4} className="px-2 py-1 text-start">{t("materiality.historyCols.new")}</ResizableTh>
                    <ResizableTh index={5} className="px-2 py-1 text-start">{t("materiality.historyCols.reason")}</ResizableTh>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {doc.revision_history.map((r, i) => (
                    <tr key={i} data-testid={`mat-history-row-${i}`}>
                      <td className="px-2 py-1 whitespace-nowrap font-mono text-[10px]">{fmtWhen(r.date)}</td>
                      <td className="px-2 py-1">{r.user_name || "—"}</td>
                      <td className="px-2 py-1" data-testid={`mat-history-field-${i}`}>{fieldLabel(t, r.field)}</td>
                      <td className="px-2 py-1">{fmtVal(r.old)}</td>
                      <td className="px-2 py-1">{fmtVal(r.new)}</td>
                      <td className="px-2 py-1 text-muted-foreground">{overrideLabel(r.reason)}</td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});

export default MaterialityFiguresPanel;
